import { useSyncExternalStore } from "react";
import { LENS_LOAD_ERROR_CODES, LENS_LOAD_STAGES, asLensLoadError, serializeLensLoadError, } from "./lensLoadError.js";
function recordOf(value) {
    return value !== null && typeof value === "object" ? value : null;
}
const LENS_ERROR_PROTOCOL = "lens-error-monitor/v1";
const DEFAULT_MAX_ISSUES = 200;
const DEFAULT_FRAME_PUBLISH_INTERVAL_MS = 1_000;
const LENSCORE_ERROR_TYPE_NAMES = Object.freeze({
    0: "LensDeserialization",
    1: "Validation",
    2: "Uncategorized",
    3: "LensExecution",
    4: "Abort",
    5: "Uninitialized",
    6: "VideoPlaybackFailed",
    7: "VideoPlaybackMuted",
});
const LENSCORE_CLASSIFICATION = Object.freeze({
    LensDeserialization: { code: "LENS_DESERIALIZATION", severity: "error", action: "none" },
    Validation: { code: "LENS_VALIDATION_FAILED", severity: "error", action: "none" },
    Uncategorized: { code: "RUNTIME_EXCEPTION", severity: "error", action: "none" },
    LensExecution: { code: "LENS_EXECUTION_FAILED", severity: "error", action: "none" },
    Abort: { code: "ENGINE_ABORTED", severity: "fatal", action: "destroy-session" },
    Uninitialized: { code: "ENGINE_UNINITIALIZED", severity: "error", action: "none" },
    VideoPlaybackFailed: { code: "VIDEO_PLAYBACK_FAILED", severity: "warning", action: "none" },
    VideoPlaybackMuted: { code: "VIDEO_PLAYBACK_MUTED", severity: "warning", action: "prompt-for-playback" },
});
function errorMessage(value) {
    const record = recordOf(value);
    if (record && "message" in record)
        return String(record.message || value);
    return String(value || "Unknown LensCore error");
}
function stableHash(value) {
    let hash = 0x811c9dc5;
    for (let index = 0; index < value.length; index += 1) {
        hash ^= value.charCodeAt(index);
        hash = Math.imul(hash, 0x01000193);
    }
    return (hash >>> 0).toString(16).padStart(8, "0");
}
/** Keep issue details useful after JSON, postMessage, WebSocket, or IPC. */
export function toDiagnosticValue(value, seen = new WeakSet(), depth = 0) {
    if (value === null || typeof value === "string" || typeof value === "number" || typeof value === "boolean")
        return value;
    if (value === undefined)
        return "undefined";
    if (typeof value === "bigint" || typeof value === "symbol" || typeof value === "function")
        return String(value);
    if (depth >= 6)
        return `[${value?.constructor?.name || "Object"}]`;
    if (typeof value !== "object")
        return String(value);
    if (seen.has(value))
        return "[Circular]";
    seen.add(value);
    if (value instanceof Error) {
        return {
            name: value.name,
            message: value.message,
            stack: value.stack ?? null,
            ...(value.cause === undefined ? {} : { cause: toDiagnosticValue(value.cause, seen, depth + 1) }),
        };
    }
    if (ArrayBuffer.isView(value))
        return { type: value.constructor.name, byteLength: value.byteLength };
    if (value instanceof ArrayBuffer)
        return { type: "ArrayBuffer", byteLength: value.byteLength };
    if (Array.isArray(value))
        return value.slice(0, 100).map((entry) => toDiagnosticValue(entry, seen, depth + 1));
    const result = {};
    const record = value;
    for (const key of Object.keys(record).slice(0, 100)) {
        try {
            result[key] = toDiagnosticValue(record[key], seen, depth + 1);
        }
        catch (error) {
            result[key] = `[Unreadable: ${errorMessage(error)}]`;
        }
    }
    if (!Object.keys(result).length && value.constructor?.name && value.constructor.name !== "Object") {
        result.type = value.constructor.name;
        try {
            result.description = String(value);
        }
        catch { /* best effort */ }
    }
    return result;
}
function findLensCoreErrorMetadata(value) {
    const queue = [{ value, isFrameError: false }];
    const seen = new Set();
    for (let depth = 0; queue.length && depth < 12; depth += 1) {
        const current = queue.shift();
        if (!current)
            break;
        const entry = recordOf(current.value);
        if (!entry || typeof entry !== "object" || seen.has(entry))
            continue;
        seen.add(entry);
        const isFrameError = current.isFrameError || entry.isFrameError === true;
        const typeValue = recordOf(entry.type)?.value;
        if (typeof typeValue === "number" && Number.isInteger(typeValue) && typeValue in LENSCORE_ERROR_TYPE_NAMES) {
            return { type: typeValue, isFrameError };
        }
        if (/^LensCore.+Error$/.test(String(entry.name || ""))) {
            const name = String(entry.name).slice("LensCore".length, -"Error".length);
            const match = Object.entries(LENSCORE_ERROR_TYPE_NAMES).find(([, known]) => known === name);
            if (match)
                return { type: Number(match[0]), isFrameError };
        }
        queue.push({ value: entry.cause, isFrameError }, { value: entry.details, isFrameError }, { value: entry.error, isFrameError }, { value: entry.value, isFrameError });
    }
    return null;
}
/**
 * Framework-neutral, bounded LensCore health and error store.
 *
 * The core provider feeds it lifecycle, structured exception, abort, and load
 * events. Optional diagnostics may contribute deeper WebGL, WASM, or QuickJS
 * evidence through acceptDiagnostic() and acceptUriEvent(). Observation never
 * performs recovery; recommendedAction is advice for the host application.
 */
export class LensErrorMonitor {
    #now;
    #maxIssues;
    #framePublishIntervalMs;
    #includeShaderSources;
    #issueListeners = new Set();
    #stateListeners = new Set();
    #issues = new Map();
    #lensId = null;
    #loadGeneration = 0;
    #loadState = "idle";
    #resourcesLoaded = false;
    #firstFrameSeen = false;
    #frameCount = 0;
    #lastFramePublishedAt = null;
    #snapshotCache = null;
    constructor(options = {}) {
        this.#now = options.now ?? Date.now;
        this.#maxIssues = Math.max(1, Math.trunc(Number(options.maxIssues ?? DEFAULT_MAX_ISSUES)));
        this.#framePublishIntervalMs = Math.max(0, Number(options.framePublishIntervalMs ?? DEFAULT_FRAME_PUBLISH_INTERVAL_MS));
        this.#includeShaderSources = options.includeShaderSources === true;
        if (options.onIssue)
            this.#issueListeners.add(options.onIssue);
    }
    subscribe = (listener) => {
        this.#stateListeners.add(listener);
        listener(this.get());
        return () => this.#stateListeners.delete(listener);
    };
    onIssue(listener) {
        this.#issueListeners.add(listener);
        return () => this.#issueListeners.delete(listener);
    }
    beginLensLoad(lensId = null) {
        this.#lensId = lensId == null ? null : String(lensId);
        this.#loadGeneration += 1;
        this.#loadState = "loading";
        this.#resourcesLoaded = false;
        this.#firstFrameSeen = false;
        this.#frameCount = 0;
        this.#lastFramePublishedAt = null;
        for (const issue of this.#issues.values())
            issue.active = false;
        this.#publishState();
    }
    lensLoaded(lensId = this.#lensId) {
        this.#lensId = lensId == null ? null : String(lensId);
        this.#loadState = "loaded";
        this.#publishState();
    }
    lensEvent(name, detail) {
        const record = recordOf(detail);
        if (record?.lensId)
            this.#lensId = String(record.lensId);
        if (name === "resourcesLoaded")
            this.#resourcesLoaded = true;
        if (name === "firstFrameProcessed")
            this.#firstFrameSeen = true;
        if (name === "turnedOff" && record?.lensId === this.#lensId)
            this.#retireCurrentLens();
        else
            this.#publishState();
    }
    frameProcessed(count) {
        this.#frameCount = Number.isFinite(count) ? Number(count) : this.#frameCount + 1;
        const firstFrame = this.#loadState === "loaded" && !this.#firstFrameSeen;
        if (this.#loadState === "loaded")
            this.#firstFrameSeen = true;
        const now = this.#now();
        if (firstFrame || this.#lastFramePublishedAt === null || now - this.#lastFramePublishedAt >= this.#framePublishIntervalMs) {
            this.#lastFramePublishedAt = now;
            this.#publishState();
        }
    }
    lensRemoved(lensId) {
        if (lensId == null || this.#lensId === String(lensId))
            this.#retireCurrentLens();
    }
    lensesCleared() {
        this.#retireCurrentLens();
    }
    markLoadFailed() {
        this.#loadState = "failed";
        this.#publishState();
    }
    loadFailed(error) {
        const normalized = asLensLoadError(error, { lensId: this.#lensId });
        const serialized = serializeLensLoadError(normalized);
        this.#loadState = "failed";
        const code = normalized.code === LENS_LOAD_ERROR_CODES.UNKNOWN
            ? "ENGINE_LOAD_FAILED"
            : normalized.code;
        return this.reportIssue({
            code,
            severity: "fatal",
            source: "host",
            message: normalized.message,
            recoverable: normalized.recoverable !== false,
            details: {
                ...serialized,
                ...(normalized.cause === undefined ? {} : { original: normalized.cause }),
            },
        });
    }
    engineInitializationFailed(error) {
        return this.reportIssue({
            code: "ENGINE_INITIALIZATION_FAILED",
            severity: "fatal",
            source: "host",
            message: errorMessage(error),
            recoverable: false,
            recommendedAction: "destroy-session",
            details: error,
        });
    }
    engineAborted(error) {
        return this.reportIssue({
            code: "ENGINE_ABORTED",
            severity: "fatal",
            source: "lenscore-native",
            message: `LensCore aborted: ${errorMessage(error)}`,
            recoverable: false,
            recommendedAction: "destroy-session",
            key: "ENGINE_ABORTED",
            details: error,
        });
    }
    runtimeError(error) {
        const lensCore = findLensCoreErrorMetadata(error);
        if (lensCore) {
            const name = LENSCORE_ERROR_TYPE_NAMES[lensCore.type];
            const classification = LENSCORE_CLASSIFICATION[name];
            const action = lensCore.isFrameError && classification.action === "none"
                ? "remove-lens"
                : classification.action;
            if (name === "Abort")
                return this.engineAborted(error);
            const normalized = asLensLoadError(error, {
                stage: LENS_LOAD_STAGES.RUNTIME,
                code: LENS_LOAD_ERROR_CODES.RUNTIME_EXCEPTION,
                lensId: this.#lensId,
                originalPreserved: false,
            });
            return this.reportIssue({
                code: classification.code,
                severity: classification.severity,
                source: "lenscore-native",
                message: normalized.message || `LensCore reported ${name}`,
                recoverable: classification.severity !== "fatal",
                recommendedAction: action,
                key: `LENSCORE_ERROR:${lensCore.type}:${normalized.message}`,
                details: {
                    lensCoreType: { name, value: lensCore.type },
                    isFrameError: lensCore.isFrameError,
                    error: serializeLensLoadError(normalized),
                    original: error,
                },
            });
        }
        const normalized = asLensLoadError(error, {
            stage: LENS_LOAD_STAGES.RUNTIME,
            code: LENS_LOAD_ERROR_CODES.RUNTIME_EXCEPTION,
            lensId: this.#lensId,
            originalPreserved: false,
        });
        const code = normalized.code === LENS_LOAD_ERROR_CODES.REMOTE_ASSET_FAILED
            ? "REMOTE_ASSET_FAILED"
            : normalized.code === LENS_LOAD_ERROR_CODES.FIRST_FRAME_TIMEOUT
                ? "FIRST_FRAME_TIMEOUT"
                : "RUNTIME_EXCEPTION";
        return this.reportIssue({
            code,
            severity: code === "FIRST_FRAME_TIMEOUT" ? "fatal" : "error",
            source: "host",
            message: normalized.message,
            recoverable: normalized.recoverable !== false,
            key: code === "REMOTE_ASSET_FAILED"
                ? `${code}:${recordOf(normalized.details)?.assetId ?? errorMessage(normalized.details)}`
                : undefined,
            details: { error: serializeLensLoadError(normalized), original: error },
        });
    }
    acceptEngineLog(message, detail) {
        const record = recordOf(detail);
        switch (message) {
            case "Lens applied":
            case "Lens added":
                this.lensLoaded(record?.lensId == null ? this.#lensId : String(record.lensId));
                return null;
            case "Lens resources loaded":
                this.lensEvent("resourcesLoaded", detail);
                return null;
            case "Lens first frame processed":
                this.lensEvent("firstFrameProcessed", detail);
                return null;
            case "Lens apply failed":
            case "Lens add failed":
                return this.loadFailed(record?.error ?? detail ?? message);
            case "Lens remote asset failed":
                return this.runtimeError(asLensLoadError(new Error("A remote lens asset could not be loaded"), {
                    stage: LENS_LOAD_STAGES.RUNTIME,
                    code: LENS_LOAD_ERROR_CODES.REMOTE_ASSET_FAILED,
                    lensId: record?.lensId == null ? this.#lensId : String(record.lensId),
                    details: detail,
                }));
            case "LensCore exception":
                return this.runtimeError(detail ?? message);
            case "LensCore aborted":
                return this.engineAborted(detail);
            case "LensCore ERR": {
                const text = Array.isArray(detail) ? detail.join(" ") : errorMessage(detail);
                if (!/error|fail|exception|abort/i.test(text))
                    return null;
                return this.reportIssue({
                    code: "NATIVE_STDERR",
                    severity: "warning",
                    source: "lenscore-native",
                    message: text,
                    recoverable: true,
                    key: `NATIVE_STDERR:${text}`,
                    details: detail,
                });
            }
            default:
                return null;
        }
    }
    acceptUriEvent(entry) {
        const record = recordOf(entry);
        if (record?.phase !== "event" || record.name !== "lens-runtime/error")
            return null;
        const body = recordOf(record.body) ?? {};
        return this.reportIssue({
            code: "SCRIPT_ASYNC_EXCEPTION",
            severity: "error",
            source: "lens-script",
            message: String(body.message || "A Lens QuickJS task rejected"),
            recoverable: true,
            key: `SCRIPT_ASYNC_EXCEPTION:${String(body.name || "Error")}:${String(body.message || "")}`,
            details: { protocol: body.protocol, name: body.name, stack: body.stack, source: body.source },
        });
    }
    /** Accept evidence from optional version-specific WASM/WebGL diagnostics. */
    acceptDiagnostic(event) {
        switch (event.kind) {
            case "shader-compile":
                return this.reportIssue({
                    code: "SHADER_COMPILE_REJECTED",
                    severity: "debug",
                    source: "webgl",
                    message: event.log || `LensCore rejected shader ${event.shader}`,
                    recoverable: true,
                    key: `SHADER_COMPILE_REJECTED:${event.shader}`,
                    details: this.#includeShaderSources ? event : { ...event, source: undefined, sourceLength: event.source?.length },
                });
            case "program-link":
                return this.reportIssue({ code: "PROGRAM_LINK_REJECTED", severity: "debug", source: "webgl", message: event.log || `LensCore rejected program ${event.program}`, recoverable: true, key: `PROGRAM_LINK_REJECTED:${event.program}`, details: event });
            case "error-pattern":
                return this.reportIssue({ code: "ERROR_PATTERN_COMPILED", severity: "warning", source: "lenscore-native", message: "LensCore compiled its magenta checker error material", recoverable: true, key: "ERROR_PATTERN_COMPILED", details: event });
            case "error-pattern-draw":
                return this.reportIssue({ code: "ERROR_PATTERN_DRAWN", severity: "warning", source: "lenscore-native", message: "LensCore issued a draw using its magenta checker material", recoverable: true, key: "ERROR_PATTERN_DRAWN", details: event });
            case "gl-error":
                return this.reportIssue({ code: "GL_ERROR", severity: "warning", source: "webgl", message: `WebGL reported error 0x${Number(event.code).toString(16)}`, recoverable: true, key: `GL_ERROR:${event.code}`, details: event });
            case "framebuffer-status":
                return this.reportIssue({ code: "FRAMEBUFFER_INCOMPLETE", severity: "error", source: "webgl", message: `WebGL framebuffer is incomplete: 0x${Number(event.status).toString(16)}`, recoverable: true, key: `FRAMEBUFFER_INCOMPLETE:${event.target}:${event.status}`, details: event });
            case "abort":
                return this.engineAborted(event.value);
            case "instantiate-failed":
                return this.reportIssue({ code: "RUNTIME_INSTANTIATION_FAILED", severity: "fatal", source: "host", message: `LensCore WASM instantiation failed: ${errorMessage(event.error)}`, recoverable: false, recommendedAction: "destroy-session", details: event });
            case "stdio":
                if (event.fd !== 2 || !/error|fail|exception|abort/i.test(event.text))
                    return null;
                return this.reportIssue({ code: "NATIVE_STDERR", severity: "warning", source: "lenscore-native", message: event.text.trim(), recoverable: true, key: `NATIVE_STDERR:${event.text.trim()}`, details: event });
            default:
                return null;
        }
    }
    /** Backward-compatible name used by the optional native diagnostics probe. */
    acceptNative(event) {
        return this.acceptDiagnostic(event);
    }
    reportIssue(input) {
        const now = this.#now();
        const lensId = input.lensId ?? this.#lensId;
        const key = input.key ?? `${input.code}:${input.message}`;
        const id = `${this.#loadGeneration}:${lensId ?? "unknown"}:${input.code}:${stableHash(key)}`;
        const existing = this.#issues.get(id);
        const details = input.details === undefined ? undefined : toDiagnosticValue(input.details);
        const issue = existing
            ? { ...existing, active: true, count: existing.count + 1, lastSeenAt: now, details: details ?? existing.details }
            : {
                id,
                code: input.code,
                severity: input.severity,
                source: input.source,
                message: input.message,
                lensId,
                recoverable: input.recoverable,
                recommendedAction: input.recommendedAction ?? "none",
                active: true,
                count: 1,
                firstSeenAt: now,
                lastSeenAt: now,
                ...(details === undefined ? {} : { details }),
            };
        this.#issues.set(id, issue);
        this.#trimIssues();
        this.#snapshotCache = null;
        const snapshot = this.get();
        for (const listener of this.#issueListeners) {
            try {
                listener(Object.freeze({ ...issue }), snapshot);
            }
            catch { /* telemetry must never break rendering */ }
        }
        this.#publishState(snapshot);
        return Object.freeze({ ...issue });
    }
    get = () => {
        if (this.#snapshotCache)
            return this.#snapshotCache;
        const issues = Object.freeze([...this.#issues.values()].map((issue) => Object.freeze({ ...issue })));
        const activeIssues = Object.freeze(issues.filter((issue) => issue.active));
        let health;
        if (this.#loadState === "failed" || activeIssues.some((issue) => issue.severity === "fatal"))
            health = "failed";
        else if (this.#loadState === "idle")
            health = "idle";
        else if (this.#loadState === "loading")
            health = "loading";
        else if (activeIssues.some((issue) => issue.severity === "error" || issue.severity === "warning"))
            health = "degraded";
        else
            health = "healthy";
        this.#snapshotCache = Object.freeze({
            protocol: LENS_ERROR_PROTOCOL,
            health,
            lensId: this.#lensId,
            loadGeneration: this.#loadGeneration,
            loadState: this.#loadState,
            resourcesLoaded: this.#resourcesLoaded,
            firstFrameSeen: this.#firstFrameSeen,
            frameCount: this.#frameCount,
            issues,
            activeIssues,
            lastIssue: issues.at(-1) ?? null,
        });
        return this.#snapshotCache;
    };
    snapshot() {
        return this.get();
    }
    clear() {
        this.#issues.clear();
        this.#publishState();
    }
    destroy() {
        this.#stateListeners.clear();
        this.#issueListeners.clear();
    }
    #retireCurrentLens() {
        this.#lensId = null;
        this.#loadState = "idle";
        this.#resourcesLoaded = false;
        this.#firstFrameSeen = false;
        this.#frameCount = 0;
        for (const issue of this.#issues.values())
            issue.active = false;
        this.#publishState();
    }
    #trimIssues() {
        while (this.#issues.size > this.#maxIssues) {
            const inactive = [...this.#issues].find(([, issue]) => !issue.active);
            const key = inactive?.[0] ?? this.#issues.keys().next().value;
            if (key !== undefined)
                this.#issues.delete(key);
        }
    }
    #publishState(snapshot) {
        if (!snapshot) {
            this.#snapshotCache = null;
            snapshot = this.get();
        }
        for (const listener of this.#stateListeners) {
            try {
                listener(snapshot);
            }
            catch { /* observers must never break rendering */ }
        }
    }
}
export function createLensErrorMonitor(options = {}) {
    return new LensErrorMonitor(options);
}
const identitySelector = (state) => state;
export function useLensErrorsFromStore(store, selector = identitySelector) {
    return useSyncExternalStore(store.subscribe, () => selector(store.get()), () => selector(store.get()));
}
export { DEFAULT_FRAME_PUBLISH_INTERVAL_MS, DEFAULT_MAX_ISSUES, LENSCORE_ERROR_TYPE_NAMES, LENS_ERROR_PROTOCOL, };
//# sourceMappingURL=lensErrors.js.map